/*
 * Harlowe Store Locator (vanilla JS)
 * Works with the provided HTML/CSS structure.
 *
 * How it finds dealer data (in order):
 * 1) window.HARLOWE_DEALERS  (define via a Custom Liquid block or section)
 * 2) <script type="application/json" id="dealers-json">[ ... ]</script>
 *
 * IMPORTANT:
 * - This file assumes Google Maps JS API + Places is already on the page.
 *   (PageFly usually injects it). We wait until google.maps.places exists.
 * - Do NOT include another Google Maps script tag if PageFly is injecting one.
 */
(function () {
  const SELECTORS = {
    mapDesktop: '#map',
    mapMobile: '#mapH5',
    list: '#showlist',
    input: '#pac-input',
    btn: '.search-btn',
    country: '#country-select',
    city: '#city-select',
  };

  const state = {
    dealers: [], // full dataset
    filtered: [], // filtered by dropdowns
    center: { lat: 39.5, lng: -98.35 }, // default: CONUS center
    map: null,
    mapH5: null,
    markers: [],
    infoWindow: null,
    selectedCountry: '',
    selectedCity: '',
    userPoint: null, // last searched/selected place
    autocomplete: null,
  };

  // ---- Bootstrap ----
  onReady(waitForMapsThenInit);

  function onReady(cb) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb);
    } else {
      cb();
    }
  }

  function waitForMapsThenInit() {
    let tries = 0;
    const maxTries = 120; // ~12s
    const timer = setInterval(() => {
      tries++;
      if (window.google && google.maps && google.maps.places) {
        clearInterval(timer);
        safeInit();
      } else if (tries >= maxTries) {
        clearInterval(timer);
        console.warn('[StoreLocator] Google Maps JS API with Places not detected.');
      }
    }, 100);
  }

  function safeInit() {
    state.dealers = normalizeDealers(loadDealers());

    // If we have dealers, choose a better default center (average of coords)
    if (state.dealers.length) {
      state.center = meanLatLng(state.dealers);
    }

    // Create maps (desktop + mobile)
    state.map = createMap(SELECTORS.mapDesktop, state.center, 4);
    state.mapH5 = createMap(SELECTORS.mapMobile, state.center, 4);

    state.infoWindow = new google.maps.InfoWindow();

    // UI bindings
    wireAutocomplete();
    wireFilters();
    wireSearchButton();

    // Initial population
    populateCountryOptions();
    applyFiltersAndRender();
  }

  // ---- Data loading ----
  function loadDealers() {
    if (Array.isArray(window.HARLOWE_DEALERS)) return window.HARLOWE_DEALERS;
    const el = document.getElementById('dealers-json');
    if (el) {
      try {
        return JSON.parse(el.textContent.trim());
      } catch (e) {
        console.error('[StoreLocator] Failed to parse #dealers-json', e);
      }
    }
    console.warn('[StoreLocator] No dealer data found (HARLOWE_DEALERS or #dealers-json).');
    return [];
  }

  function normalizeDealers(items) {
    return (items || []).map((d, i) => ({
      id: d.id || String(i + 1),
      name: d.name || d.title || 'Dealer',
      address: d.address || '',
      city: d.city || '',
      country: d.country || '',
      phone: d.phone || d.tel || '',
      website: d.website || d.url || '',
      lat: Number(d.lat ?? d.latitude ?? 0),
      lng: Number(d.lng ?? d.longitude ?? 0),
    })).filter(d => isFinite(d.lat) && isFinite(d.lng) && (d.lat || d.lng));
  }

  // ---- Maps ----
  function createMap(selector, center, zoom) {
    const el = document.querySelector(selector);
    if (!el) return null;
    return new google.maps.Map(el, {
      center,
      zoom: zoom ?? 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });
  }

  function setBoundsToMarkers(map, markers, fallbackCenter) {
    if (!map) return;
    const bounds = new google.maps.LatLngBounds();
    let count = 0;
    markers.forEach(m => {
      if (m && m.getPosition) {
        bounds.extend(m.getPosition());
        count++;
      }
    });
    if (state.userPoint) {
      bounds.extend(state.userPoint);
      count++;
    }
    if (count) {
      map.fitBounds(bounds, 60);
    } else if (fallbackCenter) {
      map.setCenter(fallbackCenter);
      map.setZoom(4);
    }
  }

  // ---- UI: Autocomplete & Filters ----
  function wireAutocomplete() {
    const input = document.querySelector(SELECTORS.input);
    if (!input) return;

    state.autocomplete = new google.maps.places.Autocomplete(input, {
      fields: ['geometry', 'formatted_address', 'name'],
    });

    state.autocomplete.addListener('place_changed', () => {
      const place = state.autocomplete.getPlace();
      if (!place || !place.geometry || !place.geometry.location) return;
      const loc = place.geometry.location;
      state.userPoint = { lat: loc.lat(), lng: loc.lng() };
      // Sort by distance to userPoint, then render
      applyFiltersAndRender();
    });

    // Let ENTER on the input trigger the place selection if any
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    });
  }

  function wireSearchButton() {
    const btn = document.querySelector(SELECTORS.btn);
    const input = document.querySelector(SELECTORS.input);
    if (!btn || !input) return;

    btn.addEventListener('click', () => {
      // If autocomplete already picked a place, applyFiltersAndRender already ran.
      // Otherwise we can try a simple geocode as a fallback.
      const val = input.value && input.value.trim();
      if (!val) return;
      if (state.userPoint) return; // already handled by place_changed

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: val }, (results, status) => {
        if (status === 'OK' && results[0] && results[0].geometry) {
          const loc = results[0].geometry.location;
          state.userPoint = { lat: loc.lat(), lng: loc.lng() };
          applyFiltersAndRender();
        }
      });
    });
  }

  function wireFilters() {
    const countrySel = document.querySelector(SELECTORS.country);
    const citySel = document.querySelector(SELECTORS.city);

    if (countrySel) {
      countrySel.addEventListener('change', () => {
        state.selectedCountry = countrySel.value || '';
        populateCityOptions();
        state.selectedCity = '';
        if (citySel) citySel.value = '';
        applyFiltersAndRender();
      });
    }

    if (citySel) {
      citySel.addEventListener('change', () => {
        state.selectedCity = citySel.value || '';
        applyFiltersAndRender();
      });
    }
  }

  function populateCountryOptions() {
    const countrySel = document.querySelector(SELECTORS.country);
    if (!countrySel) return;
    const countries = unique(
      state.dealers.map(d => (d.country || '').trim()).filter(Boolean)
    ).sort();

    // Keep existing default options; only append missing ones
    const existing = new Set(Array.from(countrySel.options).map(o => o.value));
    countries.forEach(c => {
      if (!existing.has(c)) {
        const opt = document.createElement('option');
        opt.value = c; opt.textContent = c;
        countrySel.appendChild(opt);
      }
    });
  }

  function populateCityOptions() {
    const citySel = document.querySelector(SELECTORS.city);
    if (!citySel) return;

    const isCountrySelected = !!state.selectedCountry;
    citySel.disabled = !isCountrySelected;

    // Clear all except first option
    while (citySel.options.length > 1) citySel.remove(1);

    if (!isCountrySelected) return;

    const cities = unique(
      state.dealers
        .filter(d => eqi(d.country, state.selectedCountry))
        .map(d => (d.city || '').trim())
        .filter(Boolean)
    ).sort();

    cities.forEach(c => {
      const opt = document.createElement('option');
      opt.value = c; opt.textContent = c;
      citySel.appendChild(opt);
    });
  }

  // ---- Filtering & Rendering ----
  function applyFiltersAndRender() {
    // 1) filter
    state.filtered = state.dealers.filter(d =>
      (!state.selectedCountry || eqi(d.country, state.selectedCountry)) &&
      (!state.selectedCity || eqi(d.city, state.selectedCity))
    );

    // 2) sort by distance if we have a user point
    if (state.userPoint && state.filtered.length) {
      state.filtered.forEach(d => d._distance = haversineKm(state.userPoint, d));
      state.filtered.sort((a, b) => (a._distance || 0) - (b._distance || 0));
    }

    // 3) render markers + list
    renderMarkers();
    renderList();
  }

  function renderMarkers() {
    // Clear old markers
    state.markers.forEach(m => m && m.setMap && m.setMap(null));
    state.markers = [];

    const maps = [state.map, state.mapH5].filter(Boolean);

    state.filtered.forEach((d, idx) => {
      maps.forEach((mapp) => {
        const marker = new google.maps.Marker({
          position: { lat: d.lat, lng: d.lng },
          map: mapp,
          title: d.name,
        });

        marker.addListener('click', () => {
          openInfoWindow(d, marker);
          highlightListItem(d.id);
        });

        // keep reference per desktop map only so list interactions can use first map
        if (mapp === state.map) {
          state.markers.push(marker);
        }
      });
    });

    // Fit bounds
    setBoundsToMarkers(state.map, state.markers, state.center);
    setBoundsToMarkers(state.mapH5, state.markers, state.center);
  }

  function openInfoWindow(dealer, anchorMarker) {
    if (!state.infoWindow) return;
    const html = infoWindowHTML(dealer);
    state.infoWindow.setContent(html);
    state.infoWindow.open({ map: anchorMarker.getMap(), anchor: anchorMarker });
  }

  function renderList() {
    const ul = document.querySelector(SELECTORS.list);
    if (!ul) return;

    ul.innerHTML = '';

    if (!state.filtered.length) {
      const li = document.createElement('li');
      li.innerHTML = '<div class="item"><div class="info">No dealers found. Try a different country/city or search again.</div></div>';
      ul.appendChild(li);
      return;
    }

    state.filtered.forEach((d, idx) => {
      const li = document.createElement('li');
      li.dataset.id = d.id;
      li.innerHTML = listItemHTML(d);

      li.addEventListener('click', () => {
        // Find corresponding marker (by index or nearest)
        const marker = state.markers[idx];
        if (marker) {
          state.map && state.map.panTo(marker.getPosition());
          state.map && state.map.setZoom(Math.max(12, state.map.getZoom()));
          openInfoWindow(d, marker);
        }
        highlightListItem(d.id);
      });

      ul.appendChild(li);
    });
  }

  function highlightListItem(id) {
    const ul = document.querySelector(SELECTORS.list);
    if (!ul) return;
    ul.querySelectorAll('li').forEach(li => li.classList.remove('li-select'));
    const target = ul.querySelector(`li[data-id="${cssEscape(id)}"]`);
    if (target) target.classList.add('li-select');
  }

  // ---- Templates ----
  function infoWindowHTML(d) {
    const addr = escapeHTML(d.address);
    const site = d.website ? `<div class="info-box"><a href="${escapeAttr(d.website)}" target="_blank" rel="noopener">Website</a></div>` : '';
    const tel = d.phone ? `<div class="info-box"><a href="tel:${escapeAttr(d.phone)}">${escapeHTML(d.phone)}</a></div>` : '';
    const gdir = `<div class="info-box"><a href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(d.lat + ',' + d.lng)}" target="_blank" rel="noopener">Directions</a></div>`;
    return `
      <div class="info-window-content">
        <div style="font-weight:600;">${escapeHTML(d.name)}</div>
        <div>${addr}</div>
        ${tel}
        ${site}
        ${gdir}
      </div>
    `;
  }

  function listItemHTML(d) {
    const distance = (typeof d._distance === 'number') ? ` · ${d._distance.toFixed(1)} km` : '';
    return `
      <div class="item">
        <div class="info">
          <div style="font-weight:600;">${escapeHTML(d.name)}${distance}</div>
          <div>${escapeHTML(d.address)}</div>
          <div>${escapeHTML([d.city, d.country].filter(Boolean).join(', '))}</div>
          <div class="link-box">
            ${d.phone ? `<a href="tel:${escapeAttr(d.phone)}">${escapeHTML(d.phone)}</a>` : ''}
            ${d.website ? ` · <a href="${escapeAttr(d.website)}" target="_blank" rel="noopener">Website</a>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  // ---- Utils ----
  function unique(arr) { return Array.from(new Set(arr)); }
  function eqi(a, b) { return String(a || '').toLowerCase() === String(b || '').toLowerCase(); }
  function meanLatLng(items) {
    let lat = 0, lng = 0; const n = items.length || 1;
    items.forEach(d => { lat += Number(d.lat) || 0; lng += Number(d.lng) || 0; });
    return { lat: lat / n, lng: lng / n };
  }

  function haversineKm(a, b) {
    const R = 6371; // km
    const dLat = toRad((b.lat) - (a.lat));
    const dLng = toRad((b.lng) - (a.lng));
    const lat1 = toRad(a.lat); const lat2 = toRad(b.lat);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
    return 2 * R * Math.asin(Math.sqrt(h));
  }
  function toRad(v) { return v * Math.PI / 180; }

  function escapeHTML(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function escapeAttr(str) {
    return String(str || '').replace(/"/g, '&quot;');
  }
  // CSS.escape polyfill-ish for ids/attrs in selectors
  function cssEscape(str) {
    return String(str).replace(/([#;.\\:[\](),])/g, '\\$1');
  }
})();